import type { LiveInventoryItem } from '@/types/api/live-inventory'

import { generateFilterObject } from '@moment/trading-types/filter-expression'
import { BondStatus } from '@moment/trading-types/reference-data'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Input from '@/components/atomic/input'
import { BodySmall, BodySubtext } from '@/components/atomic/text'
import { BondTypeBadge } from '@/components/molecules/badges/bond-type'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { useGetLiveInventoryPostQuery } from '@/legacy-api/data-slice'
import allLiveInventoryReturnValues from '@/utils/live-inventory/return-values'

import {
  BondSearchInputContainer,
  BondSearchInputDropdown,
  BondSearchInputResultItem,
  BondSearchInputResultMessage,
  BondSearchInputResultsList,
} from './styles'

interface BondSearchInputProps {
  value: string
  onChange: (value: string) => void
  size?: 'small' | 'large'
  required?: boolean
  onBondSelect?: (bond: LiveInventoryItem) => void
  disabled?: boolean
  suppliedRiskGroupId?: string
  className?: string
  label?: string // Add optional label prop
  dropdownPlacement?: 'up' | 'down'
}

export function BondSearchInput({
  value,
  onChange,
  size = 'large',
  required = false,
  onBondSelect,
  disabled = false,
  suppliedRiskGroupId,
  className,
  label = 'Bond', // Default to 'Bond' but allow override
  dropdownPlacement = 'up',
}: BondSearchInputProps) {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLUListElement>(null)
  const [debouncedTerm, setDebouncedTerm] = useState(value)

  // Debounce input
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(value), 300)
    return () => clearTimeout(timeout)
  }, [value])

  // Get risk group id
  const { riskGroupId: defaultRiskGroupId, isLoading: isRiskLoading } =
    useUserRiskGroupId()

  const riskGroupId = suppliedRiskGroupId || defaultRiskGroupId

  // Prepare API args
  const statusFilter = {
    status: { in: [BondStatus.Outstanding, BondStatus.PreIssuance] },
  }
  const filters = generateFilterObject(null, debouncedTerm, statusFilter)
  const apiArgs = riskGroupId
    ? {
        filters,
        returns: allLiveInventoryReturnValues,
        page: 1,
        page_size: 10,
        risk_group_id: riskGroupId,
      }
    : undefined

  const shouldQuery = !!debouncedTerm && !!riskGroupId
  const { data, isLoading, isError } = useGetLiveInventoryPostQuery(
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
    shouldQuery ? apiArgs! : (apiArgs as any),
    { skip: !shouldQuery },
  )
  const results: LiveInventoryItem[] = useMemo(() => data?.data || [], [data])

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      onChange(val)
      setOpen(!!val)
    },
    [onChange],
  )

  const handleResultClick = (
    bond: LiveInventoryItem,
    event?: React.MouseEvent,
  ) => {
    if (event) event.preventDefault()
    const selectedValue = bond.isin || bond.cusip || ''
    if (value !== selectedValue) {
      onChange(selectedValue)
    } else {
      // still call onChange to ensure parent is notified
      onChange(selectedValue)
    }
    if (typeof onBondSelect === 'function') onBondSelect(bond)
    setOpen(false)
    inputRef.current?.blur()
  }

  // Keyboard navigation
  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    setFocusedIndex(results.length > 0 ? 0 : -1)
  }, [value, results.length, open])

  useEffect(() => {
    if (focusedIndex < 0 || !resultsRef.current) return
    const el = resultsRef.current.children[focusedIndex] as
      | HTMLElement
      | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((i) => (i < results.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((i) => (i > 0 ? i - 1 : results.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0 && focusedIndex < results.length) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        handleResultClick(results[focusedIndex]!)
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <BondSearchInputContainer>
      <Input
        ref={inputRef}
        label={label}
        required={required}
        size={size}
        placeholder="Search ISIN, CUSIP, or issuer"
        value={value}
        onChange={handleInput}
        onFocus={() => setOpen(value.length > 0)}
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
        className="w-full"
        autoComplete="off"
        disabled={isRiskLoading || disabled}
      />
      {open && (
        <BondSearchInputDropdown
          className={className}
          placement={dropdownPlacement}
        >
          <BondSearchInputResultsList ref={resultsRef}>
            {isLoading && (
              <BondSearchInputResultMessage variant="loading">
                Loading…
              </BondSearchInputResultMessage>
            )}
            {isError && (
              <BondSearchInputResultMessage variant="error">
                Error loading bonds
              </BondSearchInputResultMessage>
            )}
            {!isLoading && !isError && results.length === 0 && (
              <BondSearchInputResultMessage variant="empty">
                No results
              </BondSearchInputResultMessage>
            )}
            {results.map((bond, idx) => (
              <BondSearchInputResultItem
                key={bond.isin || bond.cusip}
                isFocused={focusedIndex === idx}
                onMouseDown={(e) => handleResultClick(bond, e)}
              >
                <div>
                  <BodySmall>{bond.description_short}</BodySmall>
                  <BodySubtext className="text-secondary">
                    {bond.isin || bond.cusip}
                  </BodySubtext>
                </div>
                <BondTypeBadge bondType={bond.type} />
              </BondSearchInputResultItem>
            ))}
          </BondSearchInputResultsList>
        </BondSearchInputDropdown>
      )}
    </BondSearchInputContainer>
  )
}
